import type React from 'react';
import { useEffect, useState } from 'react';
import { adminApi } from '@/admin/adminApi';
import type { ComparisonSettings } from '@/types/domain';

const FIELDS: Array<{ key: keyof ComparisonSettings; label: string; hint: string; step: number }> = [
  { key: 'topChestWeight', label: 'Tops — chest/bust weight', hint: 'Spec default: 0.65', step: 0.05 },
  { key: 'topWaistWeight', label: 'Tops — waist weight', hint: 'Spec default: 0.35', step: 0.05 },
  { key: 'bottomWaistWeight', label: 'Bottoms — waist weight', hint: 'Spec default: 0.55', step: 0.05 },
  { key: 'bottomHipWeight', label: 'Bottoms — hip/seat weight', hint: 'Spec default: 0.45', step: 0.05 },
  { key: 'tightThresholdCm', label: 'Tight threshold (cm)', hint: 'Spec default: -1.5', step: 0.1 },
  { key: 'looseThresholdCm', label: 'Loose threshold (cm)', hint: 'Spec default: 1.5', step: 0.1 },
];

export function AdminSettingsPage() {
  const [settings, setSettings] = useState<ComparisonSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    adminApi.getSettings().then((res) => {
      if (res.success) setSettings(res.data);
    });
  }, []);

  async function handleSave() {
    if (!settings) return;
    setSaving(true);
    const res = await adminApi.updateSettings(settings);
    setSaving(false);
    if (res.success) {
      setSettings(res.data);
      setSavedAt(Date.now());
    }
  }

  if (!settings) return <p className="text-sm text-muted">Loading…</p>;

  return (
    <div className="max-w-lg space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-ink">Comparison settings</h2>
        <p className="mt-1 text-sm text-muted">
          These weights and thresholds drive every comparison across the whole app immediately —
          test carefully before saving.
        </p>
      </div>

      <div className="space-y-4 rounded-2xl border border-stone-200 bg-white p-4">
        {FIELDS.map((field) => (
          <div key={field.key}>
            <label htmlFor={field.key} className="block text-sm font-medium text-ink">
              {field.label}
            </label>
            <p className="text-xs text-muted">{field.hint}</p>
            <input
              id={field.key}
              type="number"
              step={field.step}
              value={settings[field.key]}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSettings({ ...settings, [field.key]: Number(e.target.value) })}
              className="mt-1 min-h-[44px] w-full rounded-xl border border-stone-200 px-3 py-2 text-sm"
            />
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        className="min-h-[44px] rounded-xl bg-brand px-6 py-2.5 font-semibold text-ink shadow-card disabled:opacity-40"
      >
        {saving ? 'Saving…' : 'Save settings'}
      </button>
      {savedAt && <p className="text-sm text-brand">Saved.</p>}
    </div>
  );
}
